// Iter57 — Admin SEO Landing Pages List
// Central CMS for every /plans/{facet}/{slug} landing page. Shows AI +
// approval status per row, facet filter, priority stars, and quick jump
// to the per-page editor.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Sparkles, Star, ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";

const FACETS = ["all", "style", "category", "collection"];

const AdminLandingPages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [facet, setFacet] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (facet !== "all") params.facet_type = facet;
      const { data } = await api.get("/admin/landing", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Failed to load landing pages", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [facet]);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/landing/seed");
      if (data.created_count > 0) {
        toast.success(`Created ${data.created_count} landing page(s)`, {
          description: data.skipped ? `${data.skipped} were already present.` : undefined,
        });
      } else {
        toast.info("Everything up to date", { description: `${data.skipped} landing pages already exist.` });
      }
      load();
    } catch (e) {
      toast.error("Seed failed", { description: e.response?.data?.detail || e.message });
    } finally { setSeeding(false); }
  };

  return (
    <AdminLayout title="SEO Landing Pages">
      <div className="max-w-7xl">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="overline text-primary">Content Management</div>
            <h1 className="font-display text-3xl md:text-4xl mt-1">SEO Landing Pages</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Dedicated indexable pages for every Style, Category and Collection. Each page carries an AI-generated intro and SEO metadata that only publishes after admin approval — the same strict workflow used for AI Content on plans.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={seed} variant="outline" className="rounded-none" disabled={seeding} data-testid="landing-seed-btn">
              {seeding ? <><Loader2 size={14} className="mr-2 animate-spin" />Seeding…</> : <><RefreshCw size={14} className="mr-2" />Seed / sync</>}
            </Button>
          </div>
        </div>

        {/* Facet filter */}
        <div className="flex gap-2 mb-4" data-testid="landing-facet-filter">
          {FACETS.map((f) => (
            <button
              key={f}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.14em] border rounded-none transition-colors ${facet === f ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"}`}
              onClick={() => setFacet(f)}
              data-testid={`landing-facet-${f}`}
            >
              {f === "all" ? "All" : `${f.charAt(0).toUpperCase()}${f.slice(1)}s`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground py-16 text-center">Loading landing pages…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border p-10 text-center" data-testid="landing-empty">
            <div className="text-sm text-muted-foreground">No landing pages yet. Click <strong>Seed / sync</strong> to create one page per active style, category, and collection.</div>
          </div>
        ) : (
          <div className="border border-border bg-white overflow-hidden" data-testid="landing-table">
            <div className="grid grid-cols-12 px-4 py-3 border-b border-border bg-neutral-50 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
              <div className="col-span-4">Landing page</div>
              <div className="col-span-2">Facet</div>
              <div className="col-span-2">AI status</div>
              <div className="col-span-1">Priority</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {items.map((lp) => {
              const aiReady = lp.has_ai_draft;
              const approvedCount = lp.approved_field_count || 0;
              const status = lp.status || "active";
              return (
                <div key={lp.id} className="grid grid-cols-12 px-4 py-3 border-b border-border last:border-0 items-center hover:bg-neutral-50" data-testid={`landing-row-${lp.id}`}>
                  <div className="col-span-4 min-w-0">
                    <div className="font-medium text-sm truncate">{lp.title || lp.facet_slug}</div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">{lp.url_path}</div>
                  </div>
                  <div className="col-span-2 text-xs">
                    <span className="px-2 py-0.5 border border-border bg-background">{lp.facet_type}</span>
                    <div className="text-[11px] text-muted-foreground mt-1 font-mono truncate">{lp.facet_slug}</div>
                  </div>
                  <div className="col-span-2 text-xs">
                    {aiReady ? (
                      approvedCount === 3 ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12} /> Fully approved</span>
                      ) : approvedCount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle size={12} /> {approvedCount}/3 approved</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-neutral-500"><Sparkles size={12} /> Draft pending review</span>
                      )
                    ) : (
                      <span className="inline-flex items-center gap-1 text-neutral-500"><Sparkles size={12} /> Not generated</span>
                    )}
                  </div>
                  <div className="col-span-1 flex" title={`Priority ${lp.priority}/5`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={12} className={n <= (lp.priority || 3) ? "fill-amber-500 text-amber-500" : "text-neutral-300"} />
                    ))}
                  </div>
                  <div className="col-span-1 text-xs">
                    <span className={`px-2 py-0.5 border rounded-none ${status === "active" ? "border-emerald-300 text-emerald-800 bg-emerald-50" : "border-neutral-300 text-neutral-600 bg-neutral-50"}`}>
                      {status}
                    </span>
                  </div>
                  <div className="col-span-2 text-right flex justify-end gap-2">
                    <a href={lp.url_path} target="_blank" rel="noreferrer" className="p-1.5 border border-border hover:border-primary rounded-none" title="Open public page" data-testid={`landing-open-${lp.id}`}>
                      <ExternalLink size={14} />
                    </a>
                    <Link to={`/admin/landing/${lp.id}/edit`} className="px-3 py-1.5 border border-primary text-primary text-xs uppercase tracking-[0.12em] rounded-none hover:bg-primary hover:text-primary-foreground transition-colors" data-testid={`landing-edit-${lp.id}`}>Edit</Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLandingPages;
